import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Check, FileUp, Globe, Lightbulb, Link2, Loader2, Minus, Plus, Sparkles, Trash2, X } from "lucide-react";
import { aiStatus, aiUploadMaterial, aiReadPage, aiDraft, aiGetProfile, aiSaveProfile, aiIdeas, aiIdeaDraft } from "../lib/api";
import { emptyImage, savePostWithImage } from "../lib/savePost";
import ImagePanel, { imageNode } from "./ImagePanel";
import { GOAL_LABELS, LANGUAGES, PLATFORMS, TONE_LABELS, platformLabel } from "../lib/platforms";
import ModelPicker from "./ModelPicker";
import { CharMeter, PostPreview, RewriteBar } from "./PostParts";

const errText = (e, fallback) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : fallback; };
const readAsBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(",")[1] || "");
  r.onerror = reject;
  r.readAsDataURL(file);
});

function Segmented({ value, onChange, options, testid }) {
  return <div className="segmented" data-testid={testid}>{options.map(([k, label]) => <button key={String(k)} type="button" className={value === k ? "on" : ""} onClick={() => onChange(k)}>{label}</button>)}</div>;
}

function ModelBadge({ used }) {
  if (!used) return null;
  return <span className={`model-badge ${used.free ? "free" : ""}`} title={used.fell_back ? "The first choice was out of free quota, so this one wrote it" : undefined}>
    <Sparkles size={10} /> {used.label} · {used.model}{used.free ? " · free" : ""}{used.fell_back ? " · took over" : ""}</span>;
}

// One platform's result: its versions, an editable draft with a live preview, one-click improvements, and Save.
function PlatformResult({ platform, versions, model, onSaved }) {
  const p = PLATFORMS[platform];
  const [version, setVersion] = useState(0);
  const [edits, setEdits] = useState(() => versions.map(v => ({ text: v.text, headline: v.headline, sub: v.sub, saved: false, image: emptyImage() })));
  const [view, setView] = useState("edit");
  const [aiLabel, setAiLabel] = useState(true);
  const [busy, setBusy] = useState(false);
  const draft = versions[version], e = edits[version];
  const set = (patch) => setEdits(list => list.map((x, i) => i === version ? { ...x, ...patch } : x));

  const save = async () => {
    setBusy(true);
    try {
      const { post, notes } = await savePostWithImage({ text: e.text, title: e.headline, category: p?.label || platform, aiLabel, image: e.image,
        headline: e.headline, sub: e.sub, tag: p?.label });
      set({ saved: true }); onSaved(post);
      toast.success("Saved as a post — review it, then publish");
      notes.forEach(n => toast.message(n));
    } catch (err) { toast.error(errText(err, "Could not save the post")); }
    finally { setBusy(false); }
  };

  return <article className="result-card" style={{ "--plat": p?.color || "#6366f1" }} data-testid={`ai-draft-${platform}`}>
    <header>
      <span className="plat-dot">{p?.glyph}</span><b>{platformLabel(platform)}</b>
      <ModelBadge used={draft.model} />
      {versions.length > 1 && <div className="version-tabs">{versions.map((_, i) => <button key={i} className={i === version ? "on" : ""} onClick={() => setVersion(i)} data-testid={`ai-version-${platform}-${i + 1}`}>Version {i + 1}</button>)}</div>}
    </header>
    {draft.unverified_numbers.length > 0 && <div className="ai-warn" data-testid="ai-unverified">Not in your material, check before posting: <b>{draft.unverified_numbers.join(", ")}</b></div>}
    {draft.placeholders.length > 0 && <div className="ai-warn">Needs your input: {draft.placeholders.join(" ")}</div>}
    <Segmented value={view} onChange={setView} options={[["edit", "Edit"], ["preview", "Preview"]]} />
    {view === "edit" ? <>
      <textarea value={e.text} onChange={ev => set({ text: ev.target.value })} rows={8} disabled={e.saved} />
      <CharMeter text={e.text} platform={platform} />
      {!e.saved && <RewriteBar text={e.text} platform={platform} model={model} onResult={(t) => set({ text: t })} />}
      <ImagePanel image={e.image} onChange={(image) => set({ image })} text={e.text} headline={e.headline} sub={e.sub} tag={p?.label}
        onHeadline={(headline) => set({ headline })} onSub={(sub) => set({ sub })} textModel={model} disabled={e.saved} />
    </> : <PostPreview platform={platform} text={e.text} poster={imageNode(e.image, e.headline, e.sub, p?.label)} />}
    {draft.facts_used.length > 0 && <details><summary>Where the facts came from</summary><ul>{draft.facts_used.map((f, i) => <li key={i}>{f}</li>)}</ul></details>}
    <footer>
      <label className="check-inline"><input type="checkbox" checked={aiLabel} onChange={ev => setAiLabel(ev.target.checked)} disabled={e.saved} /> Mark as AI-assisted</label>
      <button className="primary-button" onClick={save} disabled={busy || e.saved || !e.text.trim()} data-testid={`ai-save-${platform}`}>
        {busy ? <Loader2 size={14} className="spin" /> : e.saved ? <Check size={14} /> : null}{e.saved ? "Saved" : "Save as post"}</button>
    </footer>
  </article>;
}

export default function SocialAiModal({ close, onSaved }) {
  const [status, setStatus] = useState(null);
  const [door, setDoor] = useState("material");
  const [platforms, setPlatforms] = useState(["linkedin", "facebook", "instagram"]);
  const [goal, setGoal] = useState("awareness");
  const [tone, setTone] = useState("");
  const [variations, setVariations] = useState(1);
  const [language, setLanguage] = useState(LANGUAGES[0]);
  const [emoji, setEmoji] = useState("");
  const [hashtags, setHashtags] = useState(null);
  const [cta, setCta] = useState("");
  const [model, setModel] = useState({ provider: "auto", model: "" });
  const [files, setFiles] = useState([]);
  const [pageUrl, setPageUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [profile, setProfile] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [ideas, setIdeas] = useState([]);
  const [picked, setPicked] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [used, setUsed] = useState(null);
  const [busy, setBusy] = useState("");
  const [drag, setDrag] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    aiStatus().then(setStatus).catch(() => toast.error("Could not load AI settings"));
    aiGetProfile().then(r => { setProfile(r.profile); setQuestions(r.questions); }).catch(() => {});
  }, []);
  if (!status) return null;

  const choice = model.provider === "auto" ? undefined : model;
  const options = { platforms, goal, tone, variations, emoji, hashtags, cta, model: choice, language: language === LANGUAGES[0] ? "" : language };

  const upload = async (list) => {
    setBusy("upload");
    try {
      for (const f of list) {
        const r = await aiUploadMaterial(f.name, await readAsBase64(f));
        setFiles(fs => [...fs, r]);
        if (r.truncated) toast.message(`${r.filename} is long — only the first part is used`);
      }
    } catch (e) { toast.error(errText(e, "Could not read that file")); }
    finally { setBusy(""); if (fileRef.current) fileRef.current.value = ""; }
  };
  const addPage = async () => {
    setBusy("page");
    try { const page = await aiReadPage(pageUrl.trim()); setFiles(fs => [...fs, { ...page, page: true }]); setPageUrl(""); }
    catch (e) { toast.error(errText(e, "Could not read that page")); }
    finally { setBusy(""); }
  };
  const generate = async (key, call) => {
    setBusy(key); setDrafts([]);
    try { const r = await call(); setDrafts(r.posts); setUsed(r.model || r.posts[0]?.model || null); }
    catch (e) { toast.error(errText(e, "The AI could not do that")); }
    finally { setBusy(""); }
  };
  const write = () => generate("write", () => aiDraft({ ...options, material_ids: files.map(f => f.material_id), notes }));
  const suggest = async () => {
    setBusy("ideas"); setIdeas([]); setPicked([]);
    try { await aiSaveProfile(profile); setIdeas((await aiIdeas({ count: 10, model: choice })).ideas); }
    catch (e) { toast.error(errText(e, "The AI could not do that")); }
    finally { setBusy(""); }
  };
  const writeIdea = (idea) => generate("idea", () => aiIdeaDraft({ ...options, idea }));
  const chosen = ideas.filter(i => picked.includes(i.title));
  const writePicked = () => generate("idea", async () => {
    const posts = []; let model = null;
    for (const idea of chosen) { const r = await aiIdeaDraft({ ...options, idea }); posts.push(...r.posts); model = r.model || model; }
    return { posts, model };
  });
  const togglePick = (t) => setPicked(c => c.includes(t) ? c.filter(x => x !== t) : [...c, t]);

  const grouped = platforms.map(p => [p, drafts.filter(d => d.platform === p)]).filter(([, v]) => v.length);
  const canWrite = !!platforms.length && (files.length > 0 || notes.trim());

  return createPortal(<div className="modal-backdrop studio-backdrop"><div className="studio" data-testid="social-ai-modal">
    <header className="studio-head">
      <div><h2><Sparkles size={18} /> AI Studio</h2><p>Write posts from your own material, or get ideas. You approve every post before it goes out.</p></div>
      <button className="icon-button" onClick={close} title="Close"><X size={16} /></button>
    </header>
    <div className="studio-body">
      <aside className="studio-brief">
        <div className="brief-scroll">
        <Segmented value={door} onChange={d => { setDoor(d); setDrafts([]); }} testid="ai-doors"
          options={[["material", "From my material"], ["ideas", "Give me ideas"]]} />

        {door === "material" ? <section>
          <h3>1. What should it be about?</h3>
          <div className={`dropzone-modern ${drag ? "drag" : ""}`} onDragOver={e => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
            onDrop={e => { e.preventDefault(); setDrag(false); upload([...e.dataTransfer.files]); }} onClick={() => fileRef.current?.click()} data-testid="ai-upload">
            <FileUp size={20} /><b>{busy === "upload" ? "Reading…" : "Drop files here"}</b><small>PDF, Word, Excel, text or CSV · or click to choose</small>
          </div>
          <input ref={fileRef} type="file" multiple accept=".pdf,.docx,.xlsx,.xlsm,.txt,.md,.csv" hidden onChange={e => upload([...e.target.files])} />
          <div className="url-row"><Link2 size={14} /><input value={pageUrl} onChange={e => setPageUrl(e.target.value)} placeholder="Or paste a web page address" data-testid="ai-url"
            onKeyDown={e => e.key === "Enter" && pageUrl.trim() && addPage()} />
            <button className="outline-button" disabled={!pageUrl.trim() || busy === "page"} onClick={addPage}>{busy === "page" ? <Loader2 size={13} className="spin" /> : <Globe size={13} />} Read</button></div>
          {files.map(f => <div className="file-chip" key={f.material_id}>{f.page ? <Globe size={13} /> : <FileUp size={13} />}<b>{f.filename}</b><small>{f.chars.toLocaleString()} characters</small>
            <button onClick={() => setFiles(fs => fs.filter(x => x.material_id !== f.material_id))} title="Remove"><Trash2 size={12} /></button></div>)}
          <textarea rows={4} value={notes} onChange={e => setNotes(e.target.value)} data-testid="ai-notes"
            placeholder="Or type the details: Spring sale, 20% off all robots until 30 November. Free delivery in Victoria." />
        </section> : <section>
          <h3>1. Tell us about your business</h3>
          <p className="muted">Answer once. Ideas and posts are built from your answers, so keep them true and specific.</p>
          {profile && questions.map(q => <label key={q.key}>{q.label}<input value={profile[q.key] || ""} onChange={e => setProfile(p => ({ ...p, [q.key]: e.target.value }))} data-testid={`ai-profile-${q.key}`} /></label>)}
          <button className="outline-button" onClick={suggest} disabled={!!busy || !status.ready} data-testid="ai-ideas">{busy === "ideas" ? <Loader2 size={14} className="spin" /> : <Lightbulb size={14} />} Suggest post ideas</button>
          {ideas.map(i => <div className="idea-card" key={i.title}>
            <input type="checkbox" checked={picked.includes(i.title)} onChange={() => togglePick(i.title)} title="Pick this idea" aria-label={`Pick ${i.title}`} />
            <div><b>{i.title}</b><small>{i.category} · {i.angle}</small></div>
            <button className="outline-button" onClick={() => writeIdea(i)} disabled={!!busy || !platforms.length}>Write this</button></div>)}
        </section>}

        <section>
          <h3>2. Where will it go?</h3>
          <div className="chip-row">{status.platforms.map(p => <button key={p} type="button" className={`plat-chip ${platforms.includes(p) ? "on" : ""}`} style={{ "--plat": PLATFORMS[p]?.color }}
            onClick={() => setPlatforms(c => c.includes(p) ? c.filter(x => x !== p) : [...c, p])}><span>{PLATFORMS[p]?.glyph}</span>{platformLabel(p)}</button>)}</div>
          <label>Goal<Segmented value={goal} onChange={setGoal} options={Object.keys(status.goals).map(k => [k, GOAL_LABELS[k] || k])} /></label>
        </section>

        <section>
          <h3>3. Style</h3>
          <div className="chip-row">{[["", "From my brand"], ...Object.keys(TONE_LABELS).map(k => [k, TONE_LABELS[k]])].map(([k, label]) =>
            <button key={k} type="button" className={`tone-chip ${tone === k ? "on" : ""}`} onClick={() => setTone(k)} data-testid={`ai-tone-${k || "brand"}`}>{label}</button>)}</div>
          <div className="style-grid">
            <label>Versions per platform
              <div className="stepper-mini"><button type="button" onClick={() => setVariations(v => Math.max(1, v - 1))}><Minus size={12} /></button><b data-testid="ai-variations">{variations}</b>
                <button type="button" onClick={() => setVariations(v => Math.min(status.max_variations, v + 1))}><Plus size={12} /></button></div></label>
            <label>Language<select value={language} onChange={e => setLanguage(e.target.value)}>{LANGUAGES.map(l => <option key={l}>{l}</option>)}</select></label>
            <label>Emoji<Segmented value={emoji} onChange={setEmoji} options={[["", "Auto"], ["none", "None"], ["some", "Some"], ["lots", "Lots"]]} /></label>
            <label>Hashtags<Segmented value={hashtags} onChange={setHashtags} options={[[null, "Auto"], [true, "Yes"], [false, "No"]]} /></label>
          </div>
          <label>Call to action (optional)<input value={cta} onChange={e => setCta(e.target.value)} placeholder="e.g. Book a free site survey" /></label>
        </section>

        <section><h3>4. Which AI?</h3><ModelPicker value={model} onChange={setModel} onChanged={() => aiStatus().then(setStatus).catch(() => {})} /></section>
        </div>
        {door === "material" && <div className="studio-cta">
          {!status.ready && <small className="cta-hint">Set up an AI in step 4 first. A free Google Gemini key takes a minute.</small>}
          {status.ready && !canWrite && <small className="cta-hint">Add some material or notes above, and pick at least one platform.</small>}
          <button className="primary-button full" onClick={write} disabled={!!busy || !canWrite || !status.ready} data-testid="ai-write">
            {busy === "write" ? <Loader2 size={15} className="spin" /> : <Sparkles size={15} />} Write {platforms.length * variations} post{platforms.length * variations === 1 ? "" : "s"}</button></div>}
        {door === "ideas" && <div className="studio-cta">
          {!status.ready && <small className="cta-hint">Set up an AI in step 4 first. A free Google Gemini key takes a minute.</small>}
          {status.ready && !platforms.length && <small className="cta-hint">Pick at least one platform in step 2.</small>}
          {status.ready && platforms.length > 0 && ideas.length > 0 && !chosen.length && <small className="cta-hint">Tick the ideas you want, then write them all at once.</small>}
          {ideas.length === 0
            ? <button className="primary-button full" onClick={suggest} disabled={!!busy || !status.ready}>
                {busy === "ideas" ? <Loader2 size={15} className="spin" /> : <Lightbulb size={15} />} Suggest post ideas</button>
            : <button className="primary-button full" onClick={writePicked} disabled={!!busy || !chosen.length || !platforms.length || !status.ready} data-testid="ai-write-ideas">
                {busy === "idea" ? <Loader2 size={15} className="spin" /> : <Sparkles size={15} />} Write {chosen.length * platforms.length * variations} post{chosen.length * platforms.length * variations === 1 ? "" : "s"}</button>}</div>}
      </aside>

      <main className="studio-results">
        {!grouped.length && !busy && (!status.ready
          ? <div className="studio-empty setup" data-testid="ai-setup-card"><Sparkles size={28} /><h3>First, pick an AI</h3>
              <p>The AI writes your posts. A free one is enough to start, and your key stays on this computer.</p>
              <div className="setup-picker"><ModelPicker value={model} onChange={setModel} onChanged={() => aiStatus().then(setStatus).catch(() => {})} /></div></div>
          : <div className="studio-empty"><Sparkles size={26} /><h3>Your posts appear here</h3>
              <p>{door === "material" ? "Add some material on the left, choose where it will go and how it should sound, then click Write." : "Answer the questions on the left, click Suggest post ideas, then pick an idea to write."} Each post comes with a live preview, a poster and one-click improvements.</p>
              <div className="empty-steps">{(door === "material" ? ["Add material", "Pick platforms", "Choose a style", "Write"] : ["Describe your business", "Get ideas", "Pick one", "Write"]).map((t, i) => <span key={t}><b>{i + 1}</b> {t}</span>)}</div></div>)}
        {(busy === "write" || busy === "idea") && <div className="skeletons">{platforms.slice(0, 3).map(p => <div key={p} className="skeleton-card"><div className="skeleton-line w40" /><div className="skeleton-line" /><div className="skeleton-line" /><div className="skeleton-line w70" /></div>)}
          <small className="muted"><Loader2 size={12} className="spin" /> Writing{model.provider === "auto" ? " with the first free model" : ""}…</small></div>}
        {grouped.map(([p, versions]) => <PlatformResult key={`${p}-${versions.map(v => v.text.slice(0, 12)).join("|")}`} platform={p} versions={versions} model={model} onSaved={onSaved} />)}
        {used && grouped.length > 0 && <div className="studio-foot"><ModelBadge used={used} /></div>}
      </main>
    </div>
  </div></div>, document.body);
}
